import * as yup from "yup";
import { InferType } from "yup";
import creditCardValidator from "card-validator";
import { CardTypes } from "@/api/opb/opbTypes";

// card-validator's expirationDate check accepts 2-digit years too, but Zuora's
// Hosted Payment Page rejects them ("Invalid Year, should be 4-digital year
// value."). Enforce 4 digits here so that can never reach Zuora.
const hasFourDigitYear = (value?: string) => {
  if (!value) return true;
  const year = value.split("/")[1];
  return !!year && year.length === 4;
};

export const PaymentMethodSchema = yup
  .object({
    cardNumber: yup
      .string()
      .required("Card Number is required")
      .test(
        "test-number",
        "Card number is invalid",
        (value) => creditCardValidator.number(value).isValid
      ),
    expirationDate: yup
      .string()
      .required("Expiration Date is required")
      .test(
        "test-month",
        "Expiration month must be between 01 and 12",
        (value) => {
          if (!value) return true;
          const month = Number(value.split("/")[0]);
          return Number.isFinite(month) && month >= 1 && month <= 12;
        }
      )
      .test(
        "test-date",
        "Expiration date is invalid",
        (value) => creditCardValidator.expirationDate(value).isValid
      )
      .test(
        "test-year-format",
        "Expiration year must be 4 digits (YYYY)",
        hasFourDigitYear
      ),
    cvv: yup
      .string()
      .matches(/^[0-9]+$/, "Only numbers are allowed")
      .required("CVV/CVC is required")
      .test("test-cvv", "CVV number is invalid", (value, schema) =>
        creditCardValidator.number(schema.parent.cardNumber).card?.type ===
        CardTypes.Amex
          ? creditCardValidator.cvv(value, 4).isValid
          : creditCardValidator.cvv(value).isValid
      ),
    field_creditCardHolderName: yup
      .string()
      .required("Name On Card is required")
      .test(
        "test-cardName",
        "Card Name is invalid",
        (value) => creditCardValidator.cardholderName(value).isValid
      ),
    field_passthrough4: yup.string().default(""),
    field_passthrough5: yup.string().default(""),
    field_passthrough6: yup.string().default(""),
  })
  .required();

export type PaymentMethodSchemaType = InferType<typeof PaymentMethodSchema>;

export const EditCreditCardPaymentMethodSchema = (creditCardType?: string) =>
  yup.object({
    cardNumber: yup.string().default(""),
    expirationDate: yup
      .string()
      .default("")
      .test(
        "test-month",
        "Expiration month must be between 01 and 12",
        (value) => {
          if (!value) return true;
          const month = Number(value.split("/")[0]);
          return Number.isFinite(month) && month >= 1 && month <= 12;
        }
      )
      .test(
        "test-date",
        "Expiration date is invalid",
        (value) => creditCardValidator.expirationDate(value).isValid
      )
      .test(
        "test-year-format",
        "Expiration year must be 4 digits (YYYY)",
        hasFourDigitYear
      ),
    cvv: yup
      .string()
      .matches(/^[0-9]*$/, "Only numbers are allowed")
      .test(
        "test-cvv",
        "CVV number is invalid",
        (value) =>
          value?.length === 0 ||
          (creditCardType === CardTypes.Amex
            ? creditCardValidator.cvv(value, 4).isValid
            : creditCardValidator.cvv(value).isValid)
      ),
    defaultPayment: yup.boolean(),
    nickname: yup.string(),
    accountHolderInfo: yup
      .object({
        accountHolderName: yup.string().required("Name On Card is required"),
      })
      .required(),
  });

export type EditCreditCardPaymentMethodSchemaType = InferType<
  ReturnType<typeof EditCreditCardPaymentMethodSchema>
>;

export const EditStripePaymentMethodSchema = () =>
  yup.object({
    defaultPayment: yup.boolean(),
  });

export type EditStripePaymentMethodSchemaType = InferType<
  ReturnType<typeof EditStripePaymentMethodSchema>
>;
